/** @jsxImportSource ../../i18n */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, Crosshair, X } from 'lucide-react';
import api from '../../services/api';
import { cx } from '../../theme/cx';
import { formatDate, formatDateTime, formatPercent } from '../../theme/format';
import { Badge, Card, CardHeader, IconButton, Money, SymbolMark } from '../ui';

const levelLabel = (t) => (t.direction === 'below' ? 'sous' : 'au-dessus de');

// Price triggers the latest daily report set on the watchlist: armed ones
// with how far the price still has to go, reached ones with their date.
// Hidden when there is none or the call fails.
export const TriggerRow = ({ trigger: t, onDismiss }) => {
  const fired = t.status === 'fired';
  return (
    <li className="flex items-start gap-3 py-3 first:pt-0 last:pb-0" data-testid="trigger-row">
      <SymbolMark symbol={t.symbol} size="sm" className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <Link to={`/stock/${encodeURIComponent(t.symbol)}`} className="truncate text-sm font-medium text-fg hover:text-gold-300">{t.symbol}</Link>
          <span className="text-xs text-fg-subtle">
            {levelLabel(t)} <Money value={t.price} currency={t.currency || 'EUR'} sensitive={false} className="text-fg-body" />
          </span>
        </div>
        <p className="mt-0.5 text-sm leading-snug text-fg-muted">{t.label}</p>
        <div className="mt-1.5">
          {fired ? (
            <Badge tone="gold" icon={BellRing}>
              Atteint le {formatDateTime(t.fired_at)} à&nbsp;<Money value={t.fired_price} currency={t.currency || 'EUR'} sensitive={false} />
            </Badge>
          ) : t.distance_pct !== null && t.distance_pct !== undefined ? (
            <Badge tone={t.distance_pct <= 2 ? 'warn' : 'neutral'}>à {formatPercent(t.distance_pct, { sign: false })} du seuil</Badge>
          ) : (
            <Badge>armé, cours indisponible</Badge>
          )}
        </div>
      </div>
      {onDismiss && (
        <IconButton icon={X} size="sm" label={fired ? `Masquer le déclencheur de ${t.symbol}` : `Désarmer le déclencheur de ${t.symbol}`} tooltipSide="bottom-end" onClick={() => onDismiss(t)} />
      )}
    </li>
  );
};

export const TriggersCard = ({ className }) => {
  const [triggers, setTriggers] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    let alive = true;
    api.get('/watch-triggers')
      .then((res) => { if (alive) setTriggers(res.data?.triggers || []); })
      .catch(() => { if (alive) setTriggers([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => load(), [load]);

  const dismiss = async (t) => {
    setError('');
    try {
      await api.delete(`/watch-triggers/${t.id}`);
      setTriggers((list) => list.filter((x) => x.id !== t.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de retirer le déclencheur.');
    }
  };

  if (!triggers || triggers.length === 0) return null;
  const armed = triggers.filter((t) => t.status === 'active');
  const date = triggers[0]?.report_date;
  return (
    <Card className={className} data-testid="triggers-card">
      <CardHeader
        icon={Crosshair}
        title="Déclencheurs du panel"
        description={`Niveaux fixés par le rapport du ${formatDate(date, 'long')} sur votre watchlist, surveillés toutes les 15 minutes en séance. Aucun ordre n’est passé.`}
        actions={<Badge tone={armed.length ? 'gold' : 'neutral'}>{armed.length} {armed.length > 1 ? 'armés' : 'armé'}</Badge>}
      />
      <ul className={cx('divide-y divide-line')}>
        {triggers.map((t) => <TriggerRow key={t.id} trigger={t} onDismiss={dismiss} />)}
      </ul>
      {error && <p role="alert" className="mt-3 text-sm text-loss-300">{error}</p>}
    </Card>
  );
};

export default TriggersCard;
